import React, { useEffect, useState } from "react";
import { Package, Plus, Search, Pencil, Trash2, X, Store } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { rupiah } from "@/lib/format";
import { Card, Loading, EmptyState, PageHeader, Button, Modal, Badge, PhotoUpload } from "@/components/common";
import MaterialUsageModal from "@/components/MaterialUsageModal";
import { BarcodePrintModal, BarcodeInline, QrCode } from "@/components/BarcodePrint";

export default function Products() {
  const { can, isOwner } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [displayOpen, setDisplayOpen] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);

  const canEdit = can("products", "edit") || isOwner;
  const canCreate = can("products", "create") || isOwner;

  const load = async () => {
    setLoading(true);
    try {
      const [p, c, m] = await Promise.all([
        api.get("/products", { active_only: true }),
        api.get("/categories").catch(() => []),
        api.get("/materials", { usage: "recipe" }).catch(() => []),
      ]);
      setProducts(p); setCategories(c); setMaterials(m);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = products.filter((p) => !q || `${p.name} ${p.sku}`.toLowerCase().includes(q.toLowerCase()));

  const archive = async (p) => {
    const reason = window.prompt(`Arsipkan "${p.name}"? Alasan:`);
    if (!reason) return;
    try {
      await api.del(`/products/${p.id}`, { reason });
      toast.success("Produk diarsipkan");
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <>
      <PageHeader
        title="Produk"
        subtitle={`${products.length} produk`}
        icon={Package}
        actions={canCreate && <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setPrintOpen(true)} data-testid="print-barcode-btn"><QrCode size={16} /> Cetak Barcode</Button>
          <Button variant="outline" onClick={() => setDisplayOpen(true)} data-testid="add-display-btn"><Store size={16} /> Buat Produk Display</Button>
          <Button onClick={() => setEditing({})} data-testid="add-product-btn"><Plus size={16} /> Produk Baru</Button>
        </div>}
      />

      <Card className="p-3 mb-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk..." className="w-full h-10 pl-9 pr-3 rounded-xl border border-slate-300 outline-none focus:border-primary" data-testid="product-search" />
        </div>
      </Card>

      {loading ? <Loading /> : !filtered.length ? (
        <EmptyState icon={Package} title="Belum ada produk" message="Tambahkan produk pertama Anda." action={canCreate && <Button onClick={() => setEditing({})}><Plus size={16} /> Produk Baru</Button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((p) => (
            <Card key={p.id} className="p-4 flex gap-3" data-testid={`product-card-${p.id}`}>
              <div className="w-20 h-28 rounded-xl bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
                {p.photo_file_id || p.image_url ? <img src={p.photo_file_id ? fileUrl(p.photo_file_id) : p.image_url} alt="" className="w-full h-full object-cover" /> : <span className="text-2xl">💐</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800 truncate">{p.name}</div>
                    <div className="text-xs text-slate-400">{p.category} {p.sku && `· ${p.sku}`}</div>
                  </div>
                  {canEdit && (
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => setEditing(p)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500" data-testid={`edit-product-${p.id}`}><Pencil size={15} /></button>
                      <button onClick={() => archive(p)} className="w-8 h-8 rounded-lg hover:bg-rose-50 flex items-center justify-center text-rose-500"><Trash2 size={15} /></button>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-primary font-bold">{rupiah(p.price)}</span>
                  {p.gross_margin != null && <Badge className="bg-slate-100 text-slate-500">Margin {p.gross_margin}%</Badge>}
                </div>
                {(p.variants || []).length > 0 && <div className="text-xs text-slate-400 mt-1">{p.variants.length} varian</div>}
                {(p.recipe || []).length > 0 && <div className="text-xs text-slate-400 mt-0.5">Resep: {p.recipe.length} bahan · HPP {rupiah(p.cost_price)}</div>}
              </div>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <ProductModal product={editing} categories={categories} materials={materials} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
      {displayOpen && (
        <MaterialUsageModal type="display" onClose={() => setDisplayOpen(false)} onSaved={() => { setDisplayOpen(false); load(); }} />
      )}
      <BarcodePrintModal open={printOpen} onClose={() => setPrintOpen(false)} title="Cetak Barcode Produk" items={filtered.map((p) => ({ name: p.name, sku: p.sku, price: p.price }))} />
    </>
  );
}

function ProductModal({ product, categories, materials, onClose, onSaved }) {
  const isNew = !product.id;
  const [form, setForm] = useState({
    name: product.name || "",
    sku: product.sku || "",
    category: product.category || categories[0]?.name || "Bouquet",
    price: product.price || 0,
    description: product.description || "",
    track_stock: product.track_stock ?? true,
    min_stock: product.min_stock || 0,
    commission: product.commission || 0,
    photo_file_id: product.photo_file_id || null,
  });
  const [recipe, setRecipe] = useState((product.recipe || []).map((r) => ({ material_id: r.material_id, qty: r.qty })));
  const [busy, setBusy] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const genCode = async () => {
    if (!form.name.trim()) return toast.error("Isi nama produk dulu sebelum generate kode");
    setGenBusy(true);
    try {
      const params = { name: form.name.trim() };
      if (product.id) params.exclude_id = product.id;
      const r = await api.get("/barcode/generate", params);
      set("sku", r.code);
    } catch (e) { toast.error(e.message); } finally { setGenBusy(false); }
  };

  const addLine = () => setRecipe((r) => [...r, { material_id: materials[0]?.id || "", qty: 1 }]);
  const setLine = (i, k, v) => setRecipe((r) => r.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const rmLine = (i) => setRecipe((r) => r.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama wajib diisi");
    setBusy(true);
    try {
      const body = { ...form, price: Number(form.price), min_stock: Number(form.min_stock), commission: Number(form.commission), recipe: recipe.filter((r) => r.material_id).map((r) => ({ material_id: r.material_id, qty: Number(r.qty) })) };
      if (isNew) await api.post("/products", body);
      else await api.patch(`/products/${product.id}`, body);
      toast.success(isNew ? "Produk dibuat" : "Produk diperbarui");
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? "Produk Baru" : "Edit Produk"} maxWidth="max-w-lg">
      <div className="space-y-3">
        <PhotoUpload value={form.photo_file_id} onChange={(id) => set("photo_file_id", id)} label="Foto Produk" tall testid="pf-photo" />
        <Field label="Nama Produk"><input value={form.name} onChange={(e) => set("name", e.target.value)} className="inp" data-testid="pf-name" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="SKU / Barcode">
            <div className="flex gap-2">
              <input value={form.sku} onChange={(e) => set("sku", e.target.value)} className="inp flex-1" placeholder="Kode barcode" data-testid="pf-sku" />
              <button type="button" onClick={genCode} disabled={genBusy} className="shrink-0 h-10 px-3 rounded-xl border border-primary/40 bg-primary/5 text-primary text-sm font-medium hover:bg-primary/10 whitespace-nowrap disabled:opacity-60" data-testid="pf-gen-sku">{genBusy ? "..." : "Generate"}</button>
            </div>
          </Field>
          <Field label="Kategori">
            <select value={form.category} onChange={(e) => set("category", e.target.value)} className="inp">
              {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              {!categories.length && <option>Bouquet</option>}
            </select>
          </Field>
        </div>
        <BarcodeInline item={{ name: form.name, sku: form.sku, price: form.price }} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Harga Jual"><input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className="inp" data-testid="pf-price" /></Field>
          <Field label="Komisi Perangkai (Rp)"><input type="number" value={form.commission} onChange={(e) => set("commission", e.target.value)} className="inp" /></Field>
        </div>
        <Field label="Deskripsi"><textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className="inp" /></Field>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={form.track_stock} onChange={(e) => set("track_stock", e.target.checked)} /> Lacak stok (produk ready-stock tanpa resep)
        </label>

        <div className="border-t border-slate-100 pt-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-slate-700">Resep / BOM</span>
            <Button size="sm" variant="subtle" onClick={addLine} disabled={!materials.length}><Plus size={14} /> Bahan</Button>
          </div>
          {recipe.map((l, i) => (
            <div key={i} className="flex gap-2 mb-2">
              <select value={l.material_id} onChange={(e) => setLine(i, "material_id", e.target.value)} className="inp flex-1">
                {materials.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.unit})</option>)}
              </select>
              <input type="number" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} className="inp w-24" />
              <button onClick={() => rmLine(i)} className="w-9 h-10 rounded-xl text-rose-500 hover:bg-rose-50 flex items-center justify-center"><X size={16} /></button>
            </div>
          ))}
          {!recipe.length && <p className="text-xs text-slate-400">Tanpa resep — produk dijual sebagai stok jadi.</p>}
        </div>

        <Button className="w-full" onClick={save} disabled={busy} data-testid="pf-save">{busy ? "Menyimpan..." : "Simpan"}</Button>
      </div>
      <style>{`.inp{width:100%;height:2.5rem;padding:0 .75rem;border:1px solid #cbd5e1;border-radius:.75rem;font-size:.875rem;outline:none} .inp:focus{border-color:hsl(var(--primary))} textarea.inp{height:auto;padding:.5rem .75rem}`}</style>
    </Modal>
  );
}

const Field = ({ label, children }) => (
  <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">{label}</span>{children}</label>
);
